import { FunctionComponent } from 'react';
import './styles/NoticeThemePreview.scss';

import { Stack, Typography } from '@mui/material';

import { INoticeThemeStyle, noticeThemeBorderSx } from '@dw/models/notice';
import { componentClassName, getColorCss } from '@dw/utils';

interface INoticeThemePreviewProps {
  style: INoticeThemeStyle;
  /** Heading shown on the sample notice. Defaults to a plausible one. */
  title?: string;
  /** Body shown on the sample notice. Defaults to a plausible one. */
  text?: string;
}

/**
 * The theme, as a notice.
 *
 * This is the point of the whole editor. A studio is not choosing three hex values, they are
 * choosing what a notice on their board looks like — so the control shows exactly that: the same
 * paper, the same tape, the same shape the dancer app pins up, in the colours being edited.
 *
 * The tape is what makes it read as a notice rather than a swatch, and it is the one piece of
 * chrome here that is not the studio's colour — so it flips with the paper's lightness, the same
 * test the API uses when it tells an older app build which built-in tint this theme resembles.
 */
export const NoticeThemePreview: FunctionComponent<INoticeThemePreviewProps> = (props: INoticeThemePreviewProps) => {

  const {
    style,
    title = 'Concert tickets',
    text = 'Tickets go on sale Monday at 9am. One row per family, so let us know early if you need more.'
  } = props;

  const cn = componentClassName('notice-theme-preview');

  // Off the style, whoever measured it — the API for a saved theme, the editor for one being typed.
  const onDarkPaper = style.isDarkBackground;

  return (
    <Stack className={cn()} alignItems='center'>
      <Stack
        className={cn('paper')}
        rowGap={1}
        sx={{
          backgroundColor: style.backgroundColor,
          // Set once here and inherited by everything inside, which is what lets a dark theme flip
          // the whole card without any child knowing dark themes exist.
          color: style.textColor,
          ...noticeThemeBorderSx(style)
        }}
      >
        {/*
          Pale in BOTH states, exactly as the dancer app draws it, rather than a dark smudge on pale
          paper — a strip of tape is lighter than what it holds up. What makes it visible on
          near-white paper is the drop shadow in the stylesheet, not its own colour; it only dims on
          dark paper, where a 55% white strip would glare.
        */}
        <div
          className={cn('tape')}
          style={{
            backgroundColor: getColorCss('light', onDarkPaper ? 0.22 : 0.55),
            borderInlineColor: getColorCss('light', onDarkPaper ? 0.12 : 0.35)
          }}
        />

        <Typography variant='subtitle2' color='inherit' lineHeight={1.3}>{title}</Typography>
        <Typography variant='body2' color='inherit' lineHeight={1.45}>{text}</Typography>
      </Stack>
    </Stack>
  );
};
